import { getApiUrl } from './api';

// AI Voice Receptionist Personas & Dynamic Settings
export const AI_VOICE_PERSONAS = {
  siri: {
    id: 'siri',
    name: 'Siri',
    badge: 'Apple Style',
    icon: '🍎',
    subtitle: 'Natural, crisp, modern female tone',
    neuralVoiceId: 'en-US-AriaNeural',
    gender: 'female'
  },
  alexa: {
    id: 'alexa',
    name: 'Alexa',
    badge: 'Amazon Style',
    icon: '📦',
    subtitle: 'Warm, friendly, calm female tone',
    neuralVoiceId: 'en-US-JennyNeural',
    gender: 'female'
  },
  jarvis: {
    id: 'jarvis',
    name: 'Jarvis',
    badge: 'Iron Man Style',
    icon: '🤖',
    subtitle: 'Deep, polite British male assistant',
    neuralVoiceId: 'en-GB-RyanNeural',
    gender: 'male'
  },
  cortana: {
    id: 'cortana',
    name: 'Cortana',
    badge: 'Digital Assistant',
    icon: '⚡',
    subtitle: 'Upbeat, energetic corporate receptionist',
    neuralVoiceId: 'en-US-MichelleNeural',
    gender: 'female'
  }
};

export const getSavedVoicePersona = () => {
  if (typeof window === 'undefined') return 'siri';
  return localStorage.getItem('epicedge_ai_voice_persona') || 'siri';
};

export const saveVoiceSettings = (personaKey) => {
  if (typeof window === 'undefined') return;
  const validKey = AI_VOICE_PERSONAS[personaKey] ? personaKey : 'siri';
  localStorage.setItem('epicedge_ai_voice_persona', validKey);
  window.dispatchEvent(new CustomEvent('epic_voice_settings_changed', {
    detail: { personaKey: validKey }
  }));
};

let currentFetchController = null;

/**
 * Play high-fidelity Neural TTS audio stream directly from backend.
 * If audioContext and mixDestination are provided, routes audio to speakers AND to the call recorder stream!
 */
export const playNeuralAudio = (text, personaKey = null, onEnd = null, audioContext = null, mixDestination = null) => {
  if (typeof window === 'undefined') return null;

  stopAiVoice();

  const cleanText = (text || '')
    .replace(/[*#_~`>\[\]\(\)]/g, '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\bKES\s*(\d[\d,]*)/gi, '$1 cash')
    .replace(/\bKsh\.?\s*(\d[\d,]*)/gi, '$1 cash')
    .replace(/\b(\d[\d,]*)\s*KES\b/gi, '$1 cash')
    .replace(/\b(\d[\d,]*)\s*Ksh\.?\b/gi, '$1 cash')
    .replace(/\bKES\b/gi, 'cash')
    .replace(/\bKsh\.?\b/gi, 'cash')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!cleanText) {
    if (onEnd) onEnd();
    return null;
  }

  const activePersona = (personaKey && AI_VOICE_PERSONAS[personaKey]) ? personaKey : getSavedVoicePersona();
  const persona = AI_VOICE_PERSONAS[activePersona] ? activePersona : 'siri';
  const url = `${getApiUrl('/api/calls/tts')}?text=${encodeURIComponent(cleanText)}&persona=${encodeURIComponent(persona)}`;

  const controller = new AbortController();
  currentFetchController = controller;

  let hasEnded = false;
  const finish = () => {
    if (hasEnded) return;
    hasEnded = true;
    if (currentFetchController === controller) {
      currentFetchController = null;
    }
    if (onEnd) onEnd();
  };

  // If Web Audio API audioContext & mixDestination are provided, decode and route to both speakers AND the recording stream!
  if (audioContext && mixDestination && audioContext.state !== 'closed') {
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }

    const fallbackToSpeechSynthesis = () => {
      if (hasEnded || controller.signal.aborted) return;
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(cleanText);
          utterance.rate = 1.0;
          utterance.pitch = 1.0;
          
          const voices = window.speechSynthesis.getVoices() || [];
          const femaleVoice = voices.find(v => 
            /Google US English|Samantha|Microsoft Zira|Victoria|Karen|Fiona|Jenny|Natural/i.test(v.name)
          ) || voices.find(v => /^en/i.test(v.lang));
          if (femaleVoice) utterance.voice = femaleVoice;

          utterance.onend = finish;
          utterance.onerror = finish;
          window.speechSynthesis.speak(utterance);
          return;
        } catch (e) {
          console.warn('SpeechSynthesis fallback notice:', e);
        }
      }
      finish();
    };

    fetch(url, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`TTS server error: ${res.status}`);
        return res.arrayBuffer();
      })
      .then(buffer => {
        return new Promise((resolve, reject) => {
          audioContext.decodeAudioData(buffer, resolve, reject);
        });
      })
      .then(audioBuffer => {
        if (hasEnded || controller.signal.aborted) return;
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        
        // 1. Play out to caller's speakers/earpiece
        source.connect(audioContext.destination);

        // 2. Feed directly into call recording stream so AI voice is recorded in pristine quality!
        source.connect(mixDestination);

        window.__currentAiAudioSource = source;

        source.onended = () => {
          window.__currentAiAudioSource = null;
          finish();
        };

        source.start(0);
      })
      .catch(decodeErr => {
        if (controller.signal.aborted) return;
        console.warn('Web Audio decode notice, fallback to HTML5 Audio:', decodeErr);
        // Fallback to standard Audio element
        const audio = new Audio(url);
        window.__currentAiAudio = audio;
        audio.onended = finish;
        audio.onerror = () => {
          fallbackToSpeechSynthesis();
        };
        audio.play().catch(() => {
          fallbackToSpeechSynthesis();
        });
      });

    return;
  }

  // Standalone playback (e.g. Test Voice in Admin dashboard)
  const audio = new Audio(url);
  window.__currentAiAudio = audio;
  audio.onended = finish;
  audio.onerror = finish;
  audio.play().catch(finish);
  return audio;
};

export const stopAiVoice = () => {
  if (typeof window !== 'undefined') {
    if (currentFetchController) {
      try { currentFetchController.abort(); } catch (e) {}
      currentFetchController = null;
    }
    if (window.__currentAiAudioSource) {
      try {
        window.__currentAiAudioSource.onended = null;
        window.__currentAiAudioSource.stop();
      } catch (e) {}
      window.__currentAiAudioSource = null;
    }
    if (window.__currentAiAudio) {
      try {
        window.__currentAiAudio.onended = null;
        window.__currentAiAudio.onerror = null;
        window.__currentAiAudio.pause();
        window.__currentAiAudio.currentTime = 0;
      } catch (e) {}
      window.__currentAiAudio = null;
    }
    if (window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch (e) {}
    }
  }
};

export const testPlayPersonaVoice = (personaKey = 'siri') => {
  const sample = `Hello! I'm Epic AI, your virtual receptionist for EpicEdge Creative. How can I help you today?`;
  return playNeuralAudio(sample, personaKey);
};
